import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const { default: axiosClient, updateBook } = require('services/axios-client')

export const useUserBooksQuery = (userId) =>
  useQuery({
    queryKey: ['booksByUser', userId],
    enabled: !!userId,
    queryFn: () =>
      axiosClient.get(`/booksByUser`).then((res) => {
        console.log(res)
        return res.data
      }),
  })

// TODO: Get rid of placeholder data so all queries work the same.
export const useBookQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId],
    enabled: !!bookId,
    queryFn: () => axiosClient.get(`/book/${bookId}`).then((res) => res.data),
    placeholderData: {},
  })

export const useCoverTemplatesQuery = (filters) =>
  useQuery({
    queryKey: ['coverTemplates', filters],
    queryFn: () => {
      const str = `/templates/public${
        filters.categories.length > 0
          ? '?categories[]=' + filters.categories.join('&categories[]=')
          : ''
      }`

      return axiosClient.get(str).then((res) => res.data)
    },
  })

export const useBookCoversQuery = (bookId) =>
  useQuery({
    queryKey: ['userTemplates'],
    enabled: !!bookId,
    queryFn: () => {
      const str = `/templates/book/${bookId}`

      return axiosClient.get(str).then((res) => res.data)
    },
  })

export const useCoverTemplateCategoriesQuery = () =>
  useQuery({
    queryKey: ['coverTemplates', 'categories'],
    queryFn: () =>
      axiosClient.get(`/templates/public/categories`).then((res) => res.data),
  })

// Gets a list of all questions, with their tags, and 'status' indicating if they are assigned to the current book.
export const useQuestionCatalogQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'questions', 'catalog'],
    enabled: !!bookId,
    queryFn: () =>
      axiosClient
        .get(`/book/${bookId}/questions/catalog`)
        .then((res) => res.data),
    placeholderData: [],
  })

export const useBookStatsQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'stats'],
    enabled: !!bookId,
    queryFn: async () => {
      const res = await axiosClient.get(`/book/${bookId}/stats`)

      return res.data
    },
  })

export const useBookQuestionsQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'questions'],
    enabled: !!bookId,
    queryFn: async () => {
      const res = await axiosClient.get(`/book/${bookId}/questions`)

      return res.data
    },
    placeholderData: [],
  })

export const useBookUsersQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'users'],
    enabled: !!bookId,
    queryFn: async () => {
      const res = await axiosClient.get(`/book/${bookId}/users`)

      return res.data
    },
    placeholderData: [],
  })

export const useBookRoleQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'user-role'],
    enabled: !!bookId,
    queryFn: async () => {
      const res = await axiosClient.get(`/book/${bookId}/user-role`)

      return res.data
    },
  })

export const useLocaleTagsQuery = (locale) =>
  useQuery({
    queryKey: ['tags', locale],
    enabled: !!locale,
    queryFn: () => axiosClient.get(`/tags/${locale}`).then((res) => res.data),
  })

export const useBookTagsQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'tags'],
    enabled: !!bookId,
    queryFn: () =>
      axiosClient.get(`/book/${bookId}/tags`).then((res) => res.data),
  })

export const useExistingPreviewQuery = (bookId) =>
  useQuery({
    queryKey: ['book', bookId, 'preview'],
    enabled: !!bookId,
    queryFn: () =>
      axiosClient.get(`PDF/book/${bookId}/preview`).then((res) => res.data),
  })

export const fetchAnswerImages = async (bookId, questionId, answerId) => {
  const res = await axiosClient.get(
    `/book/${bookId}/question/${questionId}/answer/${answerId}/images`,
  )
  if (res.ok) {
    return res.data
  }

  return []
}

export const useBookAuthorQuery = (bookId) =>
  useQuery({
    queryKey: ['bookAuthor', bookId],
    enabled: !!bookId,
    queryFn: () =>
      axiosClient.get(`/book/${bookId}/authorUser`).then((res) => res.data),
  })

export const useBookTemplatesQuery = (bookId) =>
  useQuery({
    queryKey: ['book', 'templates', bookId],
    enabled: !!bookId,
    queryFn: () => axiosClient.get(`/templates/book/${bookId}`),
  })

export const useCoverQuery = (coverId) =>
  useQuery({
    queryKey: ['cover', coverId],
    enabled: !!coverId,
    queryFn: () =>
      axiosClient.get(`/covers/${coverId}`).then((res) => res.data),
  })

export const usePreviewPollingQuery = (bookId, isGenerating) =>
  useQuery({
    queryKey: ['book', bookId, 'preview'],
    enabled: !!isGenerating,
    queryFn: () =>
      axiosClient
        .post(`PDF/book/${bookId}/generate?is_print=true`)
        .then((res) => res.data),
    refetchInterval: 2000,
  })

export const usePromptOrderMutation = (bookId) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ prompts, isConfirmed }) => {
      return axiosClient.post(`/book/${bookId}/question-order`, {
        sort_data: prompts,
        isConfirmed,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['book', bookId, 'questions'] })
    },
  })
}

export const useBookMutation = (bookId) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload) => {
      // TODO: This should probably be a POST.
      return updateBook(bookId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['book', bookId] })
    },
  })
}
